import { useState } from 'react';
import { FiMenu, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useThemeSwitcher from '../../hooks/useThemeSwitcher';
import HireMeModal from '../ui/HireMeModal';

const navLinks = [
  { to: '/projects', label: 'Projects' },
  { to: '/services', label: 'Services' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const AppHeader = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [activeTheme, setTheme] = useThemeSwitcher();

  const toggleMenu = () => setShowMenu((prev) => !prev);

  const toggleHireModal = () => {
    const html = document.documentElement;
    if (showModal) {
      html.classList.remove('overflow-y-hidden');
      setShowModal(false);
    } else {
      html.classList.add('overflow-y-hidden');
      setShowModal(true);
    }
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="sticky top-0 z-[100] px-4 sm:px-0 py-4"
    >
      <div className="container mx-auto">
        <div className="nav-shell rounded-full px-5 py-3 shadow-lg shadow-black/5 dark:shadow-white/5 border border-black/5 dark:border-white/10 flex items-center justify-between gap-4">
          <Link to="/" aria-label="Go home" className="flex items-center gap-4 group">
            {/* Premium Text Logo */}
            <div className="relative flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
              <span className="font-general-extrabold text-3xl tracking-wider text-slate-900 dark:text-white transition-colors duration-500 group-hover:text-[#0f766e] dark:group-hover:text-[#D4F06B]">
                Ian<span className="text-[#0f766e] dark:text-[#D4F06B]">.</span>
              </span>
            </div>
            <div className="hidden md:block">
              <p className="font-general-bold text-base text-slate-900 dark:text-white tracking-wide">
                Ian Kangacha
              </p>
              <p className="font-general-medium text-xs text-[#0f766e] dark:text-[#D4F06B] tracking-widest uppercase mt-0.5">
                Fullstack Developer
              </p>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-2 font-general-medium">
            {navLinks.map((link) => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="text-sm xl:text-[0.95rem] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white px-4 py-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-300"
                aria-label={link.label}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <button
              type="button"
              onClick={() => setTheme(activeTheme)}
              aria-label="Theme Switcher"
              className="p-2.5 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200 transition-colors"
            >
              {activeTheme === 'dark' ? <FiMoon className="text-lg" /> : <FiSun className="text-lg" />}
            </button>
            <button
              type="button"
              onClick={toggleHireModal}
              className="btn-primary px-6 py-2.5 text-sm lg:text-[0.95rem] neon-action"
              aria-label="Open hire modal"
            >
              Hire Me
            </button>
          </div>

          <div className="sm:hidden flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTheme(activeTheme)}
              aria-label="Theme Switcher"
              className="p-2.5 rounded-full bg-black/5 dark:bg-white/5 text-slate-700 dark:text-slate-200"
            >
              {activeTheme === 'dark' ? <FiMoon className="text-lg" /> : <FiSun className="text-lg" />}
            </button>
            <button
              type="button"
              onClick={toggleMenu}
              className="p-2.5 rounded-full bg-black/5 dark:bg-white/5 text-slate-700 dark:text-slate-200"
              aria-label="Toggle menu"
            >
              {showMenu ? <FiX className="text-xl" /> : <FiMenu className="text-xl" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showMenu && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              className="sm:hidden mt-3 nav-shell rounded-3xl p-4 gap-2 flex flex-col shadow-lg border border-black/5 dark:border-white/10"
            >
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setShowMenu(false)}
                  className="block rounded-xl px-4 py-3 text-base text-slate-700 dark:text-slate-300 font-general-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  aria-label={link.label}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 mt-2 border-t border-black/5 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    setShowMenu(false);
                    toggleHireModal();
                  }}
                  className="btn-primary w-full py-3 text-base mt-2"
                  aria-label="Open hire modal"
                >
                  Hire Me
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showModal && <HireMeModal isOpen={showModal} onRequestClose={toggleHireModal} />}
    </motion.nav>
  );
};

export default AppHeader;
